/**
 * 현지 날짜 셈 · 요일 밀기 — 반복 화면 여러 곳이 각자 적던 것을 한 곳에 모았다 (2026-10-15 정리).
 *
 * ```
 * localDayNumber   현지 날짜의 일련번호 — 두 날짜의 «날짜 차»는 빼기 한 번(시각은 버린다)
 * shiftWeekday     요일 글자를 delta 일만큼 민다(목록 순서 그대로 — 정렬은 부르는 쪽 몫)
 * ```
 * ⚠ 아무것도 부르지 않는 잎 모듈이다 — `parseRecurrenceRule` ↔ `recurrence-form` 이 서로 이어져 있어,
 *   여기서 그쪽을 부르면 고리가 생긴다. 그래서 요일 목록도 부르는 쪽이 넘긴다.
 */

const DAY_MS = 86_400_000;

/** 현지 날짜의 일련번호(1970-01-01 = 0). `Date.UTC(현지 연·월·일)` 이 늘 하루의 배수라 나누기는 정수다. */
export function localDayNumber(d: Date): number {
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DAY_MS;
}

/**
 * 일정의 «마지막 날»(현지 날짜 번호). 종료가 자정 정각이면 그 전날까지다 — 「22:00 – 0:00」은 하루 안.
 * 월간 배치(monthLayout)·일간 날짜 표기·상세 날짜·채움 판정이 모두 이 셈을 쓴다.
 */
export function inclusiveEndDayNumber(start: Date, end: Date): number {
  const startDay = localDayNumber(start);
  const endDay = localDayNumber(end);
  const endsAtMidnight = end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0;
  return endsAtMidnight && endDay > startDay ? endDay - 1 : endDay;
}

/**
 * 요일 글자를 delta 일만큼 민다. `order` = `Date#getDay()` 순서(SU…SA) 의 요일 목록.
 * 목록에 없는 글자면 null — 그때 무엇을 할지(버리기·그대로 두기)는 부르는 쪽이 정한다.
 */
export function shiftWeekday<C extends string>(order: readonly C[], code: string, delta: number): C | null {
  const i = (order as readonly string[]).indexOf(code);
  return i < 0 ? null : order[(((i + delta) % 7) + 7) % 7]!;
}
